import { format, formatDistanceToNow } from 'date-fns';

/**
 * Centralized date formatting utilities for House of Kairos OS
 * 
 * CANONICAL USAGE: All date formatting throughout the application MUST use this module.
 * Import formatDate() or other functions from this file instead of using native 
 * toLocaleDateString() or toLocaleTimeString() methods.
 * 
 * This ensures uniform date output across the application and makes future 
 * format changes trivial by updating a single source.
 */

export type DateFormat = 'full' | 'date' | 'time' | 'relative' | 'short' | 'euDate' | 'euTime' | 'euDateTime';

const formatMap = {
  full: 'PPp', // "Apr 29, 2021, 7:43 PM"
  date: 'PP', // "Apr 29, 2021"
  time: 'p', // "7:43 PM"
  short: 'P', // "04/29/2021"
  // European formats commonly used in the codebase
  euDate: 'dd/MM/yyyy', // "29/04/2021" - for consistency with existing hardcoded formats
  euTime: 'HH:mm', // "19:43" - 24-hour format
  euDateTime: 'dd/MM/yyyy HH:mm', // "29/04/2021 19:43"
};

export function formatDate(date: string | Date, formatType: DateFormat = 'full'): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Handle invalid dates
    if (isNaN(dateObj.getTime())) {
      return '';
    }

    if (formatType === 'relative') {
      return formatDistanceToNow(dateObj, { addSuffix: true });
    }

    // Use local timezone for all formats to match calendar display
    // date-fns format() automatically converts UTC Date objects to local timezone
    return format(dateObj, formatMap[formatType]);
  } catch {
    // Gracefully handle any date formatting errors
    return '';
  }
}

/**
 * Formats class time range with date on first line and time on second line.
 * Bali-anchored: classes happen at the gym, so their wall-clock times are
 * rendered in Asia/Makassar regardless of the admin's browser timezone.
 */
export function formatClassTimeRange(startTime: string | Date, endTime: string | Date): string {
  if (!startTime || !endTime) return '';

  try {
    const dateStr = formatDateInBali(startTime, 'euDate');
    const startTimeStr = formatDateInBali(startTime, 'euTime');
    const endTimeStr = formatDateInBali(endTime, 'euTime');

    if (!dateStr || !startTimeStr || !endTimeStr) {
      return '';
    }

    return `${dateStr}\n${startTimeStr} - ${endTimeStr}`;
  } catch {
    return '';
  }
}

/**
 * Formats class time range in a single line, in Bali (Asia/Makassar) time.
 * If same Bali date: "dd/mm/yyyy, HH:mm - HH:mm"
 * If different Bali dates: "dd/mm/yyyy HH:mm - dd/mm/yyyy HH:mm"
 */
export function formatClassTimeRangeSingleLine(startTime: string | Date, endTime: string | Date): string {
  if (!startTime || !endTime) return '';

  try {
    const startDate = formatDateInBali(startTime, 'euDate');
    const endDate = formatDateInBali(endTime, 'euDate');
    const startTimeStr = formatDateInBali(startTime, 'euTime');
    const endTimeStr = formatDateInBali(endTime, 'euTime');

    if (!startDate || !endDate || !startTimeStr || !endTimeStr) {
      return '';
    }

    // Check if same date
    if (startDate === endDate) {
      return `${startDate}, ${startTimeStr} - ${endTimeStr}`;
    } else {
      return `${startDate} ${startTimeStr} - ${endDate} ${endTimeStr}`;
    }
  } catch {
    return '';
  }
}

/**
 * Formats date range for memberships and other date ranges
 */
export function formatDateRange(startDate: string | Date, endDate: string | Date): string {
  try {
    const start = formatDate(startDate, 'date');
    const end = formatDate(endDate, 'date');
    
    // Handle empty results from formatDate
    if (!start || !end) {
      return '';
    }
    
    return `${start} - ${end}`;
  } catch {
    return '';
  }
}

/**
 * Replaces toLocaleDateString() calls with consistent formatting
 */
export function formatLocalDate(date: string | Date): string {
  try {
    return formatDate(date, 'date');
  } catch {
    return '';
  }
}

/**
 * Formats an Odoo-sourced timestamp for display, e.g. "Jul 30, 2026, 02:15 PM".
 * Odoo returns naive UTC datetimes (no timezone suffix), so a 'Z' is appended
 * when the string carries no timezone info; tz-aware ISO strings (e.g. from
 * Supabase sync-run rows) pass through unchanged.
 */
export function formatOdooTimestamp(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('Z') || dateStr.includes('+') ? '' : 'Z'));
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formats a date with weekday and long date format
 * Example: "Monday, January 15, 2024"
 * Used for public-facing class schedule pages
 */
export function formatDateWithWeekday(date: string | Date): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Handle invalid dates
    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return format(dateObj, 'EEEE, MMMM d, yyyy'); // "Monday, January 15, 2024"
  } catch {
    return '';
  }
}

/**
 * Formats a date range for public schedule display, in Bali (Asia/Makassar)
 * time — the schedule advertises when classes happen at the gym, so a remote
 * viewer must not see it shifted into their own timezone.
 * Returns formatted weekday dates with time ranges.
 */
export function formatScheduleDateRange(startTime: string | Date, endTime: string | Date): string {
  if (!startTime || !endTime) return '';

  try {
    const start = typeof startTime === 'string' ? new Date(startTime) : startTime;

    if (isNaN(start.getTime())) {
      return '';
    }

    const dateStr = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Makassar',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(start); // "Monday, January 15, 2024"
    const startTimeStr = formatDateInBali(startTime, 'time');
    const endTimeStr = formatDateInBali(endTime, 'time');

    if (!startTimeStr || !endTimeStr) {
      return '';
    }

    return `${dateStr} • ${startTimeStr} - ${endTimeStr}`;
  } catch {
    return '';
  }
}

/**
 * Converts a datetime-local string (local time, no timezone) to ISO UTC string for database storage
 * datetime-local inputs return strings like "2025-11-22T09:00" which should be treated as LOCAL time
 */
export function convertLocalDateTimeToUTC(dateTimeLocal: string): string {
  if (!dateTimeLocal) return '';
  
  try {
    // Create a Date object treating the input as local time
    // new Date() with YYYY-MM-DDTHH:mm format interprets it as LOCAL time
    const localDate = new Date(dateTimeLocal);
    
    if (isNaN(localDate.getTime())) {
      return '';
    }
    
    // Return ISO string (UTC) for database storage
    return localDate.toISOString();
  } catch {
    return '';
  }
}

/**
 * Converts a UTC ISO string from database to datetime-local format (local time)
 * Used for populating datetime-local inputs in edit forms
 */
export function convertUTCToLocalDateTime(utcIsoString: string): string {
  if (!utcIsoString) return '';
  
  try {
    const date = new Date(utcIsoString);
    
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Get local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    // Return in datetime-local format (YYYY-MM-DDTHH:mm)
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch {
    return '';
  }
}

/**
 * Returns the calendar date key (YYYY-MM-DD) for the given instant in Bali
 * (Asia/Makassar, UTC+8, no DST). Use this to bucket timestamps by the day
 * they occurred in Bali, independent of the viewer's browser timezone.
 */
export function getBaliDateKey(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  // en-CA locale formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Makassar',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Returns the UTC instant boundaries [startUtc, endUtc) of the Bali
 * (Asia/Makassar, UTC+8, no DST) calendar day containing the given instant.
 *
 * Use this to bucket `timestamptz` columns by the day they occurred in Bali,
 * independent of the admin's browser timezone. Replaces the
 *   const today = new Date(); today.setHours(0, 0, 0, 0);
 * pattern, which silently uses browser-local midnight and produces a wrong
 * window for any admin viewing outside Asia/Makassar.
 *
 * Implementation note: WITA has no DST, so we can construct the UTC bounds
 * directly from the Bali calendar date via the literal `+08:00` offset.
 */
export function getBaliDayBoundsUtc(date: Date = new Date()): {
  startUtc: string;
  endUtc: string;
} {
  return baliDayBoundsForKey(getBaliDateKey(date));
}

/**
 * UTC bounds of the Bali calendar day named by a YYYY-MM-DD key:
 * `startUtc` inclusive, `endUtc` exclusive (start of the next Bali day).
 * Key-based twin of `getBaliDayBoundsUtc` for querying a chosen date
 * rather than "the day containing this instant".
 */
export function baliDayBoundsForKey(key: string): {
  startUtc: string;
  endUtc: string;
} {
  const start = new Date(`${key}T00:00:00+08:00`);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { startUtc: start.toISOString(), endUtc: end.toISOString() };
}

const BALI_TZ = 'Asia/Makassar';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Noon-UTC anchor for a Bali date key (= 20:00 WITA, safely the same Bali day),
// so getBaliDateKey() round-trips and ±24h arithmetic stays on the day grid.
function baliKeyAnchor(key: string): Date {
  return new Date(`${key}T12:00:00Z`);
}

/**
 * Shifts a Bali date key (YYYY-MM-DD) by a whole number of days, returning the
 * resulting Bali date key. WITA has no DST, so this is exact.
 */
export function addDaysToBaliKey(key: string, days: number): string {
  return getBaliDateKey(new Date(baliKeyAnchor(key).getTime() + days * MS_PER_DAY));
}

/**
 * Coerce a value to a Bali (Asia/Makassar) `YYYY-MM-DD` date key.
 *
 * A **date-only** string ("2026-03-15", as returned by Postgres `date` columns
 * such as membership `start_date`/`end_date` and `date_of_birth`) is ALREADY a
 * Bali calendar day and is returned verbatim — it must NOT be run through
 * timezone conversion, which would shift it. A `Date` (or a full timestamp
 * string) is an instant and is converted to the Bali calendar day it falls on.
 * Returns '' for anything unparseable.
 *
 * Use this (not `new Date(str)` + `getMonth()/getDate()`) whenever you compare a
 * stored date against "today in Bali": the naive path reads a date-only string
 * back in the admin's *browser* timezone and lands a day off outside WITA.
 */
export function toBaliDateKey(value: string | Date): string {
  if (typeof value === 'string') {
    // Already a bare Bali calendar day → keep it exactly as stored.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    // Otherwise it's an instant (timestamp) → bucket it into its Bali day.
    return getBaliDateKey(new Date(value));
  }
  // A Date (or Date-like) instant → the Bali day it falls on. getBaliDateKey
  // already guards against invalid dates. (Avoid `instanceof Date`, which is
  // unreliable across realms / fake timers.)
  return getBaliDateKey(value);
}

/**
 * Whole days from Bali date key `fromKey` to `toKey` (i.e. `toKey - fromKey`),
 * both `YYYY-MM-DD`. WITA has no DST so this is exact. Returns NaN if either key
 * is unparseable.
 */
export function diffBaliDays(fromKey: string, toKey: string): number {
  const from = Date.parse(`${fromKey}T00:00:00Z`);
  const to = Date.parse(`${toKey}T00:00:00Z`);
  if (isNaN(from) || isNaN(to)) return NaN;
  return Math.round((to - from) / MS_PER_DAY);
}

/**
 * Returns `count` consecutive Bali date keys (YYYY-MM-DD) ending on the Bali
 * calendar day containing `now`, oldest first. Use to build chart day axes that
 * are anchored to Bali regardless of the admin's browser timezone.
 */
export function baliDateKeysEndingToday(count: number, now: Date = new Date()): string[] {
  const todayKey = getBaliDateKey(now);
  const keys: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    keys.push(addDaysToBaliKey(todayKey, -i));
  }
  return keys;
}

/**
 * Formats a Bali date key (YYYY-MM-DD) as a short "MMM dd" axis label
 * (e.g. "Nov 25"), evaluated in Bali time.
 */
export function formatBaliDayLabel(key: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    month: 'short',
    day: '2-digit',
  }).format(baliKeyAnchor(key));
}

/**
 * Formats a Bali date key (YYYY-MM-DD) as "Aug 8, 2026" — the `date` style of
 * `formatDate`, but safe for date-only strings: parsing a bare key with
 * `new Date()` and formatting browser-local shifts it a day for admins west
 * of UTC. Use for `date` columns like `pt_sessions.session_date`.
 */
export function formatBaliDateKeyLabel(key: string): string {
  const anchor = baliKeyAnchor(key);
  if (isNaN(anchor.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(anchor);
}

/**
 * Formats a Bali date key (YYYY-MM-DD) as "Friday, 8 Aug" for day-group
 * headings (e.g. the Upcoming Sessions panel).
 */
export function formatBaliDayHeading(key: string): string {
  const anchor = baliKeyAnchor(key);
  if (isNaN(anchor.getTime())) return '';
  // Composed from parts: en-GB renders "Saturday 8 Aug" without the comma.
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    weekday: 'long',
  }).format(anchor);
  const dayMonth = new Intl.DateTimeFormat('en-GB', {
    timeZone: BALI_TZ,
    day: 'numeric',
    month: 'short',
  }).format(anchor);
  return `${weekday}, ${dayMonth}`;
}

/**
 * Formats a Bali date key (YYYY-MM-DD) as its full weekday name in Bali time
 * (e.g. "Monday"). Anchored via the noon-UTC trick so it round-trips on the day grid.
 */
export function formatBaliWeekday(key: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    weekday: 'long',
  }).format(baliKeyAnchor(key));
}

/**
 * Formats a UTC instant as a Bali (Asia/Makassar) 24-hour 'HH:mm' label,
 * independent of the viewer's browser timezone. Used for fixed-slot grids.
 */
export function formatBaliTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: BALI_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/**
 * Formats a timestamp as a relative datetime string anchored to Bali local time
 * (Asia/Makassar, UTC+8) regardless of the admin's browser timezone:
 *   - "Today at 7:32 AM"      when the instant falls on today's Bali calendar day
 *   - "Yesterday at 4:59 AM"  when it falls on yesterday's Bali calendar day
 *   - "May 21 at 3:14 PM"     otherwise
 */
export function formatBaliDateTime(date: string | Date, now: Date = new Date()): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);

  const key = getBaliDateKey(d);
  const todayKey = getBaliDateKey(now);
  if (key === todayKey) return `Today at ${time}`;
  if (key === addDaysToBaliKey(todayKey, -1)) return `Yesterday at ${time}`;

  const dateLabel = new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    month: 'short',
    day: 'numeric',
  }).format(d);
  return `${dateLabel} at ${time}`;
}

/**
 * Bali-anchored equivalents of the `DateFormat` styles rendered by
 * `formatDate`. Built with Intl so the output is identical for every admin,
 * regardless of their browser timezone.
 */
const BALI_DATE_FORMATTERS: Partial<Record<DateFormat, Intl.DateTimeFormat>> = {
  full: new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }),
  date: new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }),
  time: new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }),
  short: new Intl.DateTimeFormat('en-US', {
    timeZone: BALI_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }),
  euDate: new Intl.DateTimeFormat('en-GB', {
    timeZone: BALI_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }),
  euTime: new Intl.DateTimeFormat('en-GB', {
    timeZone: BALI_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }),
};

/**
 * Drop-in Bali (Asia/Makassar) twin of `formatDate`: same `DateFormat` styles,
 * same output shapes, but the wall-clock is Bali's rather than the browser's.
 * Use for anything that happened (or will happen) at the gym — class times,
 * booking/waitlist timestamps, change-log entries.
 */
export function formatDateInBali(date: string | Date, formatType: DateFormat = 'full'): string {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    // Relative distance is timezone-independent (instant vs instant).
    if (formatType === 'relative') {
      return formatDistanceToNow(dateObj, { addSuffix: true });
    }

    if (formatType === 'euDateTime') {
      return `${BALI_DATE_FORMATTERS.euDate!.format(dateObj)} ${BALI_DATE_FORMATTERS.euTime!.format(dateObj)}`;
    }

    return BALI_DATE_FORMATTERS[formatType]!.format(dateObj);
  } catch {
    return '';
  }
}

/**
 * Converts a datetime-local string representing BALI wall-clock time
 * ("2026-08-08T09:00") to an ISO UTC string for database storage.
 * WITA is fixed UTC+8 with no DST, so the literal offset is exact — same
 * trick as `baliDayBoundsForKey`. Counterpart of `convertLocalDateTimeToUTC`,
 * which interprets the input in the admin's browser timezone.
 */
export function convertBaliDateTimeToUTC(dateTimeLocal: string): string {
  if (!dateTimeLocal) return '';

  try {
    // datetime-local yields 'YYYY-MM-DDTHH:mm'; tolerate seconds if present.
    const withSeconds = /T\d{2}:\d{2}$/.test(dateTimeLocal)
      ? `${dateTimeLocal}:00`
      : dateTimeLocal;
    const date = new Date(`${withSeconds}+08:00`);

    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString();
  } catch {
    return '';
  }
}

/**
 * Converts a UTC ISO string to the Bali wall-clock datetime-local format
 * ("YYYY-MM-DDTHH:mm") for populating datetime-local inputs in edit forms.
 * Counterpart of `convertUTCToLocalDateTime`, which yields browser-local time.
 */
export function convertUTCToBaliDateTime(utcIsoString: string): string {
  if (!utcIsoString) return '';

  const d = new Date(utcIsoString);
  if (isNaN(d.getTime())) return '';

  return `${getBaliDateKey(d)}T${formatBaliTime(d)}`;
}

/**
 * Returns a Date whose BROWSER-LOCAL components equal the Bali wall-clock
 * components of the given instant. This is deliberately the "wrong" instant:
 * it exists only to feed libraries that render Dates in the browser's
 * timezone (react-big-calendar), so every admin sees the Bali schedule.
 * Never store or compare these Dates against real instants.
 */
export function baliWallClockDate(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return new Date(NaN);
  // 'YYYY-MM-DDTHH:mm' with no offset parses as browser-local time.
  return new Date(`${getBaliDateKey(d)}T${formatBaliTime(d)}`);
}

/**
 * Inverse of `baliWallClockDate`: reads a shifted Date's browser-local
 * components as a Bali wall-clock reading and returns the real instant it
 * denotes. WITA is a fixed UTC+8 with no DST, so the literal offset is exact.
 *
 * Use this at the boundary where react-big-calendar hands a grid Date back
 * (slot selection), so nothing outside the calendar ever sees a shifted Date.
 */
export function instantFromBaliWallClock(wallClock: Date): Date {
  if (!wallClock || isNaN(wallClock.getTime())) return new Date(NaN);

  const pad = (value: number) => String(value).padStart(2, '0');
  return new Date(
    `${wallClock.getFullYear()}-${pad(wallClock.getMonth() + 1)}-${pad(wallClock.getDate())}` +
      `T${pad(wallClock.getHours())}:${pad(wallClock.getMinutes())}:${pad(wallClock.getSeconds())}+08:00`
  );
}

/**
 * Default class form times: today (Bali calendar day) 09:00–10:00 Bali,
 * as datetime-local strings for inputs converted via `convertBaliDateTimeToUTC`.
 */
export function getDefaultTimes() {
  const todayKey = getBaliDateKey(new Date());
  return {
    startTime: `${todayKey}T09:00`,
    endTime: `${todayKey}T10:00`,
  };
}